import { useEffect, useState } from "react";
import { marked } from "marked";
import { Megaphone, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { listAnnouncements } from "@/lib/cms/announcements";
import { getOrganization } from "@/lib/admin/organizations";

/**
 * Parent-facing "Notes from your center" card.
 *
 * Reads published announcements via RLS (the existing
 * cms_announcements_public_read policy allows SELECT to any signed-in
 * user for rows with published_at IS NOT NULL in their org).
 *
 * Hidden entirely when there are no published announcements — the
 * parent dashboard shouldn't show an empty state, it just collapses.
 */
marked.setOptions({ gfm: true, breaks: true });

const LIMIT = 3;

export default function AnnouncementsCard() {
  const { profile } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Respect the org-wide `feature_public_announcements` toggle so
        // admins can hide the card without unpublishing existing posts.
        let enabled = true;
        if (profile?.organization_id) {
          try {
            const org = await getOrganization(profile.organization_id);
            enabled = org?.feature_public_announcements !== false;
          } catch {
            // Parents cannot SELECT organizations by default (RLS is
            // member-scoped via role_assignments). If the read fails,
            // fall back to "enabled" so the card is visible.
          }
        }
        if (!enabled) {
          setItems([]);
          return;
        }
        const list = await listAnnouncements({ status: "published" });
        setItems((list || []).slice(0, LIMIT));
      } catch {
        // Silently ignore — parent home should never spam errors for
        // a nice-to-have CMS card. The table is restricted by RLS; a
        // missing schema grant or unpublished-only state both just
        // result in 0 items.
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.organization_id]);

  if (loading || items.length === 0) return null;

  return (
    <section
      data-testid="parent-announcements-card"
      className="rounded-2xl border border-secondary/50 bg-secondary/20 p-5"
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white">
          <Megaphone size={15} weight="duotone" className="text-primary-hover" />
        </span>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-600">
          Notes from your center
        </p>
      </div>
      <ul
        data-testid="parent-announcements-list"
        className="space-y-2"
      >
        {items.map((a) => {
          const isOpen = expanded === a.id;
          return (
            <li
              key={a.id}
              data-testid={`parent-announcement-${a.id}`}
              className="rounded-2xl border border-white/70 bg-white/80 p-3.5 backdrop-blur"
            >
              <button
                type="button"
                data-testid={`parent-announcement-${a.id}-toggle`}
                onClick={() => setExpanded(isOpen ? null : a.id)}
                className="flex w-full items-start justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <p className="font-heading text-sm font-semibold text-neutral-900">
                    {a.title}
                  </p>
                  <p className="mt-0.5 text-[11px] text-neutral-500">
                    {formatRelative(a.published_at)}
                  </p>
                </div>
                <CaretDown
                  size={13}
                  weight="bold"
                  className={`mt-1 flex-shrink-0 text-neutral-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isOpen && (
                <div
                  data-testid={`parent-announcement-${a.id}-body`}
                  className="prose prose-sm mt-3 max-w-none border-t border-neutral-200 pt-3"
                  dangerouslySetInnerHTML={{
                    __html: marked.parse(a.body_md || "_No additional detail._"),
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    const diffMs = Date.now() - dt.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d ago`;
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
